import { useCallback, useState } from "react";
import { flushSync } from "react-dom";

/**
 * When a dialog shows a field's error: from the first press of Add/Save, and
 * then as the field changes. Not on leaving a field — an error appearing then
 * pushes the button down mid-click, and the browser drops the click. A field
 * spreads `field(name, id)` and `error(name, id)` renders the line under it.
 * Used by the tool and phone-number dialogs.
 */
export function useFieldErrors<Field extends string>(errors: Partial<Record<Field, string>>) {
  const [submitted, setSubmitted] = useState(false);
  const shown = (name: Field) => (submitted ? errors[name] : undefined);

  const reset = useCallback(() => setSubmitted(false), []);

  return {
    field: (name: Field, id: string) => ({
      "aria-invalid": Boolean(shown(name)),
      "aria-describedby": shown(name) ? `${id}-error` : undefined,
    }),
    error: (name: Field, id: string) => {
      const message = shown(name);
      return message ? (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {message}
        </p>
      ) : null;
    },
    /**
     * Before a save: true when nothing in `blocking` is wrong. Otherwise every
     * error shows and focus moves to the first field in `container` that has
     * one. Either way the press counts: an error that arrives later — a field
     * the server refused — shows as soon as it is passed in.
     *
     * `blocking` is the errors that stop a send, when they are not all of the
     * ones shown: a server refusal on display is the reason the last press
     * failed, not a reason to refuse this one — the dialog clears it on the press
     * and sends again.
     */
    check: (container: HTMLElement | null, blocking: Partial<Record<Field, string>> = errors) => {
      flushSync(() => setSubmitted(true));
      if (!Object.values(blocking).some(Boolean)) return true;
      container?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      return false;
    },
    reset,
  };
}
